/**
 * Grant Helper Utilities
 */

import { Person, Groups, CheckCircle, Warning, Error } from '@mui/icons-material';

/**
 * Format grant for display
 */
export function formatGrantForDisplay(grant) {
  if (!grant) return null;

  return {
    ...grant,
    displayName: grant.grantName || `Grant-${grant.id?.slice(0, 8)}`,
    targetDisplay: formatTargetDisplay(grant),
    resourceCount: grant.resources?.length || 0,
    actionCount: countActions(grant.actionRules),
    isActive: isGrantActive(grant),
    hasConflicts: false // Will be determined by conflict detection
  };
}

/**
 * Format target display string
 */
function formatTargetDisplay(grant) {
  if (grant.targetType === 'USER') {
    return `User: ${grant.targetUserId}`;
  }

  const parts = [];
  if (grant.targetGroup && grant.targetGroup !== 'ALL') {
    parts.push(`Group: ${grant.targetGroup}`);
  }
  if (grant.targetGroupRole && grant.targetGroupRole !== 'ALL') {
    parts.push(`Role: ${grant.targetGroupRole}`);
  }
  if (grant.targetOrgLevel && grant.targetOrgLevel !== 'ALL') {
    parts.push(`Level: ${grant.targetOrgLevel}`);
  }

  return parts.length > 0 ? parts.join(', ') : 'All Users';
}

/**
 * Count total actions in action rules
 */
function countActions(actionRules) {
  if (!actionRules) return 0;

  let count = 0;
  Object.values(actionRules).forEach(resourceActions => {
    count += Object.keys(resourceActions).length;
  });
  return count;
}

/**
 * Check if grant is currently active
 */
export function isGrantActive(grant) {
  if (grant.status !== 'ACTIVE') return false;

  const now = new Date().toISOString();
  if (grant.effectiveFrom && grant.effectiveFrom > now) return false;
  if (grant.effectiveTo && grant.effectiveTo <= now) return false;

  return true;
}

/**
 * Validate user access to grant management
 */
export function validateGrantAccess(user) {
  if (!user) return false;

  // Check if user has admin role or specific grant management permissions
  // Support both legacy (role: 'Admin') and new (groupRole: 'APP_ADMIN') systems
  const hasLegacyAdminRole = user.role === 'Admin';
  const hasNewAdminRole = user.groupRole === 'APP_ADMIN' || user.groupRole === 'CLIENT_ADMIN';
  const hasGrantPermission = user.permissions?.grants?.write === 'ALLOW';

  return hasLegacyAdminRole || hasNewAdminRole || hasGrantPermission;
}

/**
 * Get grant status color for MUI
 */
export function getGrantStatusColor(status) {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'default';
    case 'PENDING':
      return 'warning';
    case 'EXPIRED':
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Get grant type icon
 */
export function getGrantTypeIcon(type) {
  switch (type) {
    case 'USER':
      return <Person />;
    case 'ROLE':
      return <Groups />;
    default:
      return null;
  }
}

/**
 * Generate grant signature for deduplication
 */
export function generateGrantSignature(grant) {
  const key = [
    grant.targetType,
    grant.targetGroup || 'NONE',
    grant.targetGroupRole || 'NONE',
    grant.targetOrgLevel || 'NONE',
    grant.targetUserId || 'NONE',
    JSON.stringify(grant.resources?.sort() || []),
    JSON.stringify(grant.actionRules || {})
  ].join('#');

  return Buffer.from(key).toString('base64');
}

/**
 * Check if two grants are equivalent
 */
export function areGrantsEquivalent(grant1, grant2) {
  return generateGrantSignature(grant1) === generateGrantSignature(grant2);
}

/**
 * Detect conflicts between grants
 */
export function detectGrantConflicts(grants) {
  const conflicts = [];
  const targetMap = new Map();

  // Group grants by target
  grants.forEach(grant => {
    const targetKey = grant.targetType === 'USER'
      ? `USER:${grant.targetUserId}`
      : `ROLE:${grant.targetGroup}:${grant.targetGroupRole}:${grant.targetOrgLevel}`;

    if (!targetMap.has(targetKey)) {
      targetMap.set(targetKey, []);
    }
    targetMap.get(targetKey).push(grant);
  });

  // Check for conflicts within each target group
  targetMap.forEach((targetGrants, targetKey) => {
    if (targetGrants.length <= 1) return;

    // Check resource overlaps
    for (let i = 0; i < targetGrants.length - 1; i++) {
      for (let j = i + 1; j < targetGrants.length; j++) {
        const grant1 = targetGrants[i];
        const grant2 = targetGrants[j];

        const overlappingResources = findOverlappingResources(grant1, grant2);
        if (overlappingResources.length > 0) {
          const conflictingActions = findConflictingActions(
            grant1,
            grant2,
            overlappingResources
          );

          if (conflictingActions.length > 0) {
            conflicts.push({
              type: 'ACTION_CONFLICT',
              target: targetKey,
              grants: [grant1.id, grant2.id],
              resources: overlappingResources,
              actions: conflictingActions,
              severity: 'HIGH'
            });
          }
        }
      }
    }
  });

  return conflicts;
}

/**
 * Find overlapping resources between grants
 */
function findOverlappingResources(grant1, grant2) {
  if (!grant1.resources || !grant2.resources) return [];

  return grant1.resources.filter(r =>
    grant2.resources.includes(r)
  );
}

/**
 * Find conflicting actions between grants
 */
function findConflictingActions(grant1, grant2, resources) {
  const conflicts = [];

  resources.forEach(resource => {
    const rules1 = grant1.actionRules?.[resource] || {};
    const rules2 = grant2.actionRules?.[resource] || {};

    Object.keys(rules1).forEach(action => {
      if (rules2[action] && rules1[action] !== rules2[action]) {
        // Check if it's a real conflict (not DENY precedence)
        if (!(rules1[action] === 'DENY' || rules2[action] === 'DENY')) {
          conflicts.push({
            resource,
            action,
            grant1: { id: grant1.id, value: rules1[action], priority: grant1.priority },
            grant2: { id: grant2.id, value: rules2[action], priority: grant2.priority }
          });
        }
      }
    });
  });

  return conflicts;
}

/**
 * Sort grants by priority and specificity
 */
export function sortGrantsByPriority(grants) {
  return [...grants].sort((a, b) => {
    // Higher priority first
    if (a.priority !== b.priority) {
      return (b.priority || 1) - (a.priority || 1);
    }

    // User grants before role grants
    if (a.targetType !== b.targetType) {
      return a.targetType === 'USER' ? -1 : 1;
    }

    // More specific role grants first
    const aSpecificity = getGrantSpecificity(a);
    const bSpecificity = getGrantSpecificity(b);
    if (aSpecificity !== bSpecificity) {
      return bSpecificity - aSpecificity;
    }

    // By name
    return (a.grantName || '').localeCompare(b.grantName || '');
  });
}

/**
 * Calculate grant specificity score
 */
function getGrantSpecificity(grant) {
  if (grant.targetType === 'USER') return 100;

  let score = 0;
  if (grant.targetGroup && grant.targetGroup !== 'ALL') score += 30;
  if (grant.targetGroupRole && grant.targetGroupRole !== 'ALL') score += 30;
  if (grant.targetOrgLevel && grant.targetOrgLevel !== 'ALL') score += 30;
  return score;
}

/**
 * Filter grants by criteria
 */
export function filterGrants(grants, criteria) {
  return grants.filter(grant => {
    // Status filter
    if (criteria.status && grant.status !== criteria.status) {
      return false;
    }

    // Type filter
    if (criteria.targetType && grant.targetType !== criteria.targetType) {
      return false;
    }

    // Group filter
    if (criteria.targetGroup && grant.targetGroup !== criteria.targetGroup) {
      return false;
    }

    // Role filter
    if (criteria.targetGroupRole && grant.targetGroupRole !== criteria.targetGroupRole) {
      return false;
    }

    // Level filter
    if (criteria.targetOrgLevel && grant.targetOrgLevel !== criteria.targetOrgLevel) {
      return false;
    }

    // Resource filter
    if (criteria.resources && criteria.resources.length > 0) {
      const hasResource = grant.resources?.some(r =>
        criteria.resources.includes(r)
      );
      if (!hasResource) return false;
    }

    // Active filter
    if (criteria.activeOnly && !isGrantActive(grant)) {
      return false;
    }

    // Search term
    if (criteria.searchTerm) {
      const term = criteria.searchTerm.toLowerCase();
      const searchableText = [
        grant.grantName,
        grant.description,
        grant.targetUserId,
        grant.targetGroup,
        grant.targetGroupRole,
        grant.targetOrgLevel,
        ...(grant.resources || [])
      ].filter(Boolean).join(' ').toLowerCase();

      if (!searchableText.includes(term)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Calculate grant impact score
 */
export function calculateGrantImpact(grant) {
  let score = 0;

  // Target scope
  if (grant.targetType === 'ROLE') {
    if (grant.targetGroup === 'ALL') score += 30;
    if (grant.targetGroupRole === 'ALL') score += 30;
    if (grant.targetOrgLevel === 'ALL') score += 30;
  } else {
    score += 10; // User-specific grants have lower impact
  }

  // Resource scope
  const resourceCount = grant.resources?.length || 0;
  score += Math.min(resourceCount * 5, 50);

  // Action scope
  const actionCount = countActions(grant.actionRules);
  score += Math.min(actionCount * 3, 30);

  // Priority multiplier
  const priority = grant.priority || 1;
  score = score * (1 + priority / 10);

  return Math.round(score);
}

/**
 * Get grant summary statistics
 */
export function getGrantSummaryStats(grants) {
  const stats = {
    total: grants.length,
    active: 0,
    inactive: 0,
    pending: 0,
    userGrants: 0,
    roleGrants: 0,
    temporalGrants: 0,
    highPriorityGrants: 0,
    averagePriority: 0,
    resourceCoverage: new Set(),
    conflicts: []
  };

  let totalPriority = 0;

  grants.forEach(grant => {
    // Status counts
    if (grant.status === 'ACTIVE') stats.active++;
    else if (grant.status === 'INACTIVE') stats.inactive++;
    else if (grant.status === 'PENDING') stats.pending++;

    // Type counts
    if (grant.targetType === 'USER') stats.userGrants++;
    else stats.roleGrants++;

    // Temporal grants
    if (grant.effectiveFrom || grant.effectiveTo) {
      stats.temporalGrants++;
    }

    // High priority grants
    if ((grant.priority || 1) >= 7) {
      stats.highPriorityGrants++;
    }

    // Priority average
    totalPriority += (grant.priority || 1);

    // Resource coverage
    grant.resources?.forEach(r => stats.resourceCoverage.add(r));
  });

  stats.averagePriority = grants.length > 0
    ? (totalPriority / grants.length).toFixed(1)
    : 0;

  stats.resourceCoverage = Array.from(stats.resourceCoverage);
  stats.conflicts = detectGrantConflicts(grants);

  return stats;
}